import React, { useState, useEffect, useRef } from 'react'
import { API_BASE_URL } from '../utils/api'
import { useNavigate } from 'react-router-dom'
import { IncomingCallNotification } from './IncomingCallNotification'

const NOTIFICATION_SERVICE_URL = (import.meta as any).env?.VITE_NOTIFICATION_SERVICE_URL || 'http://localhost:3000'

interface Notification {
  id: number
  user_id?: number
  type: string
  title: string
  message: string
  metadata?: any
  priority: 'low' | 'normal' | 'high' | 'urgent'
  action_url?: string
  group_key?: string
  group_count: number
  is_read: boolean
  created_at: string
  updated_at: string
}

interface NotificationBellProps {
  className?: string
}

// Helper function to get current user ID from JWT token
const getCurrentUserId = (): number | null => {
  try {
    // First try localStorage user object (legacy)
    const userStr = localStorage.getItem('user')
    if (userStr) {
      const user = JSON.parse(userStr)
      if (user.id) return parseInt(user.id)
    }
    
    // Fallback: decode from JWT token
    const token = localStorage.getItem('token')
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return payload.userId || payload.id || null
    }
    
    return null
  } catch (error) {
    console.error('Error getting user ID:', error)
    return null
  }
}

const POLL_MIN_MS = 8000
const POLL_MAX_MS = 5 * 60 * 1000 // 5 minutes

export const NotificationBell: React.FC<NotificationBellProps> = ({ className = '' }) => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [incomingCallNotification, setIncomingCallNotification] = useState<Notification | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Backoff state — keep in refs so the setTimeout closure always sees current values
  const pollDelayRef = useRef(POLL_MIN_MS)
  const consecutiveFailuresRef = useRef(0)
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const schedulePoll = (delay: number) => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current)
    pollTimerRef.current = setTimeout(async () => {
      await fetchUnreadCount()
      schedulePoll(pollDelayRef.current)
    }, delay)
  }

  useEffect(() => {
    fetchUnreadCount()
    fetchNotifications()
    schedulePoll(pollDelayRef.current)

    // Listen for notifications being marked as read in other components
    const handleNotificationsMarkedAsRead = () => {
      fetchUnreadCount()
      if (isOpen) {
        fetchNotifications()
      }
    }

    window.addEventListener('notificationsMarkedAsRead', handleNotificationsMarkedAsRead)

    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current)
      window.removeEventListener('notificationsMarkedAsRead', handleNotificationsMarkedAsRead)
    }
  }, [isOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token')
      const userId = getCurrentUserId()
      if (!token || !userId) return

      const response = await fetch(`${NOTIFICATION_SERVICE_URL}/api/notifications/user/${userId}/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setUnreadCount(data.unreadCount || data.unread_count || 0)
        // Reset backoff on success
        consecutiveFailuresRef.current = 0
        pollDelayRef.current = POLL_MIN_MS
      } else {
        throw new Error(`HTTP ${response.status}`)
      }
    } catch (error) {
      consecutiveFailuresRef.current += 1
      // Only log the first failure to avoid console spam
      if (consecutiveFailuresRef.current === 1) {
        console.warn('Notification service unavailable — will retry with backoff')
      }
      // Exponential backoff: double each time up to POLL_MAX_MS
      pollDelayRef.current = Math.min(pollDelayRef.current * 2, POLL_MAX_MS)
    }
  }

  const fetchNotifications = async () => {
    if (loading) return

    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const userId = getCurrentUserId()
      if (!token || !userId) return

      const response = await fetch(`${NOTIFICATION_SERVICE_URL}/api/notifications/user/${userId}?limit=20&offset=0`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        
        // Check for incoming call notifications and handle them separately
        const incomingCall = data.notifications.find((notif: Notification) => 
          notif.type === 'call' && !notif.is_read
        )
        
        if (incomingCall) {
          setIncomingCallNotification(incomingCall)
        }
        
        // Set regular notifications (excluding the incoming call)
        const regularNotifications = data.notifications.filter((notif: Notification) => 
          !(notif.type === 'call' && !notif.is_read)
        )
        
        setNotifications(regularNotifications)
        setUnreadCount(data.unreadCount || data.unread_count || 0)
      }
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (notificationId: number) => {
    try {
      const token = localStorage.getItem('token')
      const userId = getCurrentUserId()
      if (!token || !userId) return

      const response = await fetch(`${NOTIFICATION_SERVICE_URL}/api/notifications/${notificationId}/mark-read`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId })
      })

      if (response.ok) {
        // Update local state
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === notificationId 
              ? { ...notif, is_read: true }
              : notif
          )
        )
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token')
      const userId = getCurrentUserId()
      if (!token || !userId) return

      const response = await fetch(`${NOTIFICATION_SERVICE_URL}/api/notifications/user/${userId}/mark-all-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        setNotifications(prev => 
          prev.map(notif => ({ ...notif, is_read: true }))
        )
        setUnreadCount(0)
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  }

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if not already read
    if (!notification.is_read) {
      await markAsRead(notification.id)
    }

    // Navigate to the action URL if available
    if (notification.action_url) {
      navigate(notification.action_url)
    }

    setIsOpen(false)
  }

  const handleIncomingCallAnswer = async () => {
    if (incomingCallNotification) {
      // Mark call notification as read
      await markAsRead(incomingCallNotification.id)
      
      // Open the call widget - trigger the call widget to show
      // You might want to integrate this with your existing call widget
      const callEvent = new CustomEvent('triggerTelnyxCall', {
        detail: {
          phoneNumber: incomingCallNotification.metadata?.phone,
          customerId: incomingCallNotification.metadata?.customer_id,
          customerName: incomingCallNotification.metadata?.customer_name
        }
      })
      window.dispatchEvent(callEvent)
      
      setIncomingCallNotification(null)
    }
  }

  const handleIncomingCallDecline = async () => {
    if (incomingCallNotification) {
      // Mark call notification as read
      await markAsRead(incomingCallNotification.id)
      setIncomingCallNotification(null)
    }
  }

  const handleIncomingCallDismiss = async () => {
    if (incomingCallNotification) {
      // Mark call notification as read
      await markAsRead(incomingCallNotification.id)
      setIncomingCallNotification(null)
    }
  }

  const toggleDropdown = () => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      fetchNotifications()
    }
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  const getNotificationStyling = (notification: Notification) => {
    // If unread, use blue styling with visible border
    if (!notification.is_read) {
      return 'border-l-4 border-l-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
    }
    
    // If read, no border and no background
    return 'border-l-0 bg-white dark:bg-gray-800'
  }

  return (
    <>
      {/* Incoming Call Notification Overlay */}
      {incomingCallNotification && incomingCallNotification.metadata && (
        <IncomingCallNotification
          notification={{
            ...incomingCallNotification,
            metadata: {
              customer_name: incomingCallNotification.metadata.customer_name || 'Unknown',
              phone: incomingCallNotification.metadata.phone || '',
              call_control_id: incomingCallNotification.metadata.call_control_id || '',
              customer_id: incomingCallNotification.metadata.customer_id
            }
          }}
          onAnswer={handleIncomingCallAnswer}
          onDecline={handleIncomingCallDecline}
          onDismiss={handleIncomingCallDismiss}
        />
      )}

      <div className={`relative ${className}`} ref={dropdownRef}>
        {/* Bell Button */}
      <button
        onClick={toggleDropdown}
        className="relative p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200"
        aria-label="Notifications"
      >
        {/* Bell Icon */}
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Notification Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center min-w-[20px] px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-50 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-sm text-gray-900 dark:text-gray-600 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mx-auto"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center">
                <div className="text-4xl mb-2">🔔</div>
                <p className="text-gray-500 dark:text-gray-400">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${getNotificationStyling(notification)}`}
                  >
                    <div className="flex items-start">
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className={`text-sm font-semibold ${
                            !notification.is_read 
                              ? 'text-gray-900 dark:text-white' 
                              : 'text-gray-700 dark:text-gray-300'
                          }`}>
                            {notification.title}
                            {notification.group_count > 1 && (
                              <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full">
                                {notification.group_count}
                              </span>
                            )}
                          </p>
                          {!notification.is_read && (
                            <div className="w-2 h-2 bg-gray-800 rounded-full flex-shrink-0"></div>
                          )}
                        </div>
                        <p className={`text-sm mt-1 line-clamp-2 ${
                          !notification.is_read 
                            ? 'text-gray-700 dark:text-gray-300' 
                            : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {notification.message}
                        </p>
                        <p className={`text-xs mt-2 ${
                          !notification.is_read 
                            ? 'text-gray-500 dark:text-gray-400'
                            : 'text-gray-500 dark:text-gray-500'
                        }`}>
                          {formatTime(notification.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 text-center">
              <button
                onClick={() => {
                  navigate('/notifications')
                  setIsOpen(false)
                }}
                className="text-sm text-gray-900 dark:text-gray-600 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
      </div>
    </>
  )
} 